import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { readAsDataUrl } from '../../shared/api';
import { Badge, Empty, ErrorBox, Field, Loading, Modal, errorText, useAsync, useToast } from '../../shared/ui';
import { api, type SapSupplier, type SupplierRow } from '../api';
import { isBuyer, usePortal } from '../context';
import { SupplierLogo } from '../components/status';

export function Proveedores(): JSX.Element {
  const { me } = usePortal();
  const editable = isBuyer(me.session.role);
  const { data, error, reload } = useAsync(() => api.get<{ suppliers: SupplierRow[] }>('/portal/suppliers'), []);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<SupplierRow | null>(null);
  // Sube al cambiar un logo para que las miniaturas se vuelvan a descargar.
  const [logoVersion, setLogoVersion] = useState(0);
  const [q, setQ] = useState('');

  const suppliers = (data?.suppliers ?? []).filter((s) => {
    const needle = q.trim().toLowerCase();
    return !needle || `${s.name} ${s.cardCode} ${s.email ?? ''}`.toLowerCase().includes(needle);
  });

  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <h1>Proveedores</h1>
          <p className="muted">
            {editable
              ? 'Proveedores de SAP con acceso al portal. Solo los dados de alta y activos ven sus pedidos.'
              : 'Proveedores de SAP dados de alta en el portal (solo consulta).'}
          </p>
        </div>
        {editable ? (
          <button className="btn primary" onClick={() => setAdding(true)}>
            Dar de alta proveedor
          </button>
        ) : null}
      </div>

      <section className="card">
        <div className="card-head">
          <input className="input pt-search" type="search" placeholder="Buscar por nombre, código o correo" aria-label="Buscar proveedor" value={q} onChange={(e) => setQ(e.target.value)} />
          {data ? <span className="subtle">{data.suppliers.length} dados de alta</span> : null}
        </div>
        {error ? (
          <div className="card-body">
            <ErrorBox error={error} onRetry={() => void reload()} />
          </div>
        ) : !data ? (
          <Loading />
        ) : data.suppliers.length === 0 ? (
          <Empty icon="🏭">
            <strong>Todavía no hay proveedores en el portal.</strong>
            <div>{editable ? 'Pulsa «Dar de alta proveedor» y búscalo en SAP por nombre o código.' : 'Compras los dará de alta.'}</div>
          </Empty>
        ) : suppliers.length === 0 ? (
          <Empty icon="🔍">Ningún proveedor coincide con «{q}».</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th aria-label="Logo" />
                  <th>Nombre</th>
                  <th>Código</th>
                  <th>Correo</th>
                  <th className="num">Usuarios</th>
                  <th className="num">Pedidos abiertos</th>
                  <th>Acceso</th>
                  <th>Valoración</th>
                  {editable ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.cardCode}>
                    <td>
                      <SupplierLogo cardCode={s.cardCode} hasLogo={s.hasLogo} name={s.name} version={logoVersion} />
                    </td>
                    <td>
                      <strong>{s.name}</strong>
                    </td>
                    <td className="mono">{s.cardCode}</td>
                    <td>{s.email ?? <span className="subtle">Sin correo</span>}</td>
                    <td className="num">
                      {editable ? <Link to={`/usuarios?proveedor=${encodeURIComponent(s.cardCode)}`}>{s.users}</Link> : s.users}
                    </td>
                    <td className="num">
                      <Link to={`/?sup=${encodeURIComponent(s.cardCode)}`}>{s.openOrders}</Link>
                    </td>
                    <td>{s.enabled ? <Badge tone="ok">Activo</Badge> : <Badge tone="bad">Sin acceso</Badge>}</td>
                    <td>{s.evalManual ? <Badge tone="accent">Fijada al 100 %</Badge> : <Badge>Automática</Badge>}</td>
                    {editable ? (
                      <td className="nowrap right">
                        <button className="btn sm" onClick={() => setEditing(s)}>
                          Editar
                        </button>{' '}
                        <Link className="btn sm ghost" to={`/usuarios?proveedor=${encodeURIComponent(s.cardCode)}`}>
                          Usuarios
                        </Link>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {adding ? (
        <AddSupplierModal existing={new Set((data?.suppliers ?? []).map((s) => s.cardCode))} onClose={() => setAdding(false)} onDone={() => void reload()} />
      ) : null}
      {editing ? (
        <EditSupplierModal
          supplier={editing}
          logoVersion={logoVersion}
          onLogoChanged={() => setLogoVersion((v) => v + 1)}
          onClose={() => setEditing(null)}
          onDone={() => void reload()}
        />
      ) : null}
    </div>
  );
}

// ── Alta desde SAP ────────────────────────────────────────────────────────

function AddSupplierModal({ existing, onClose, onDone }: { existing: Set<string>; onClose: () => void; onDone: () => void }): JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SapSupplier[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<unknown>(null);
  const [picked, setPicked] = useState<SapSupplier | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<SapSupplier | null>(null);

  // Búsqueda en SAP con pausa: no se lanza una consulta por cada tecla.
  useEffect(() => {
    if (picked) return;
    let alive = true;
    const t = window.setTimeout(() => {
      setSearching(true);
      setSearchError(null);
      api
        .get<{ suppliers: SapSupplier[] }>(`/portal/sap-suppliers?q=${encodeURIComponent(q.trim())}`)
        .then((r) => alive && setResults(r.suppliers))
        .catch((err) => alive && setSearchError(err))
        .finally(() => alive && setSearching(false));
    }, 350);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [q, picked]);

  async function submit(): Promise<void> {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/portal/suppliers', { cardCode: picked.cardCode, email: email.trim() === '' ? null : email.trim(), enabled: true });
      toast(`${picked.name} dado de alta en el portal`, 'ok');
      onDone();
      setDone(picked);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Modal
        title="Proveedor dado de alta"
        onClose={onClose}
        footer={
          <>
            <button className="btn" onClick={onClose}>
              Cerrar
            </button>
            <button className="btn primary" onClick={() => navigate(`/usuarios?proveedor=${encodeURIComponent(done.cardCode)}&nuevo=1`)}>
              Crear usuario de acceso
            </button>
          </>
        }
      >
        <div className="stack">
          <p>
            <strong>{done.name}</strong> ya está en el portal. En el próximo repaso de SAP se publicarán sus pedidos abiertos.
          </p>
          <p className="muted">Para que pueda entrar necesita al menos un usuario (un correo y una contraseña). Puede tener varios.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Dar de alta proveedor"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || !picked}>
            {busy ? 'Dando de alta…' : 'Dar de alta'}
          </button>
        </>
      }
    >
      <div className="stack">
        {picked ? (
          <>
            <div className="callout pt-picked">
              <div>
                <strong>{picked.name}</strong> <span className="mono subtle">{picked.cardCode}</span>
              </div>
              <button className="btn sm" onClick={() => setPicked(null)} disabled={busy}>
                Cambiar
              </button>
            </div>
            <Field label="Correo para los avisos (opcional)" hint="Si lo dejas vacío se usa el de la ficha de SAP.">
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            {existing.has(picked.cardCode) ? (
              <div className="callout info">Este proveedor ya estaba dado de alta: se reactivará su acceso.</div>
            ) : null}
          </>
        ) : (
          <>
            <Field label="Buscar en SAP" hint="Por nombre o código de interlocutor. Se muestran como mucho 30.">
              <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej.: Mecanizados" autoFocus />
            </Field>
            <ErrorBox error={searchError} />
            {searching && !results ? <Loading label="Buscando en SAP…" /> : null}
            {results && results.length === 0 ? <Empty icon="🔍">Ningún proveedor de SAP coincide con «{q}».</Empty> : null}
            {results && results.length > 0 ? (
              <ul className="pt-pick-list" aria-busy={searching}>
                {results.map((s) => (
                  <li key={s.cardCode}>
                    <button
                      className="pt-pick"
                      onClick={() => {
                        setPicked(s);
                        setEmail(s.email ?? '');
                      }}
                    >
                      <span>
                        <strong>{s.name}</strong> <span className="mono subtle">{s.cardCode}</span>
                        {s.email ? <span className="subtle"> · {s.email}</span> : null}
                      </span>
                      {existing.has(s.cardCode) ? <Badge tone="ok">Ya en el portal</Badge> : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}

// ── Editar: correo, acceso, valoración y logo ─────────────────────────────

function EditSupplierModal({
  supplier,
  logoVersion,
  onLogoChanged,
  onClose,
  onDone,
}: {
  supplier: SupplierRow;
  logoVersion: number;
  onLogoChanged: () => void;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const [email, setEmail] = useState(supplier.email ?? '');
  const [enabled, setEnabled] = useState(supplier.enabled);
  const [evalManual, setEvalManual] = useState(supplier.evalManual);
  const [hasLogo, setHasLogo] = useState(supplier.hasLogo);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/portal/suppliers/${encodeURIComponent(supplier.cardCode)}`, {
        email: email.trim() === '' ? null : email.trim(),
        enabled,
        evalManual,
      });
      toast(`${supplier.name} guardado`, 'ok');
      onDone();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  async function setLogo(file: File | null): Promise<void> {
    setLogoBusy(true);
    setError(null);
    try {
      const dataUrl = file ? await readAsDataUrl(file) : null;
      await api.put(`/portal/suppliers/${encodeURIComponent(supplier.cardCode)}/logo`, { dataUrl });
      setHasLogo(file !== null);
      onLogoChanged();
      onDone();
      toast(file ? 'Logo actualizado' : 'Logo quitado', 'ok');
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setLogoBusy(false);
    }
  }

  return (
    <Modal
      title={`${supplier.name} · ${supplier.cardCode}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Correo para los avisos" hint="Los pedidos nuevos, recordatorios y cambios se avisan a este correo.">
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Acceso al portal activo
        </label>
        {!enabled && supplier.enabled ? (
          <div className="callout warn">Al guardar se cerrarán las sesiones abiertas de sus usuarios y dejará de ver sus pedidos.</div>
        ) : null}
        <label className="check">
          <input type="checkbox" checked={evalManual} onChange={(e) => setEvalManual(e.target.checked)} />
          Fijar su valoración al 100 %
        </label>
        <p className="subtle">Deja de calcularse la evaluación automática para este proveedor mientras esté marcado.</p>

        <div className="divider" />
        <Field label="Logo" hint="PNG, JPEG o WebP de hasta 300 KB. Se ve en su portal y en el ranking.">
          <div className="row">
            <div className="logo-box">
              <SupplierLogo cardCode={supplier.cardCode} hasLogo={hasLogo} name={supplier.name} version={logoVersion} size={64} />
            </div>
            <div className="stack sm">
              <label className={`btn sm ${logoBusy ? 'pt-disabled' : ''}`}>
                {logoBusy ? 'Subiendo…' : hasLogo ? 'Cambiar logo' : 'Subir logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  disabled={logoBusy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void setLogo(f);
                  }}
                />
              </label>
              {hasLogo ? (
                <button className="btn sm danger" onClick={() => void setLogo(null)} disabled={logoBusy}>
                  Quitar logo
                </button>
              ) : null}
            </div>
          </div>
        </Field>
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}
